<template>
  <div class="w-full overflow-y-auto flex-1 h-0">
    <slot name="header"/>
    <template v-if="(searchedTeams && searchedTeams.length) || (searchedApprovers && searchedApprovers.length) || searchQuery">
      <div class="p-6 space-y-6 pt-2">
        <bc-approval-graph-header
          title="Approved"
          graph-class="bg-bc-blue"
          :total="approval?.stats.total"
          :approvers="approvedApprovers"
        />
        <bc-approval-graph-header
          title="Disapproved"
          graph-class="bg-bc-red"
          :total="approval?.stats.total"
          :approvers="disapprovedApprovers"
        />
        <bc-approval-graph-header
          title="Waiting"
          graph-class="bg-gray-400"
          :total="approval?.stats.total"
          :approvers="waitingApprovers"
        />
      </div>
      <bc-contractable-menu 
        class="flex flex-row justify-start items-center flex-wrap px-5 pb-2"
        :offset="30"
      >
        <template #left="{ contractLeft }">
          <nav 
            class="flex flex-row justify-start items-center flex-wrap"
            ref="filterPills"
          >
            <template v-if="!contractLeft">
              <button
                class="pill__Tab text-sm mr-1"
                :class="{'pill__Tab-active': !activeFilter}"
                @click.stop.prevent="activeFilter = null"
              >
                {{ $t('All') }}
              </button>
              <button
                v-for="filter in usableFilters" :key="filter"
                class="pill__Tab text-sm mx-1 capitalize"
                :class="{'pill__Tab-active':  activeFilter === filter}"
                @click.stop.prevent="activeFilter = filter"
              >
                {{ $t(filter) }}
              </button>
            </template>
            <aw-dropdown
              v-else
              placement="bottom-end"
              :offset="[0, 2]"
              class="relative z-20"
            >
              <template v-slot:btn="{ open }">
                <button 
                  class="capitalize"
                  @click="open"
                >
                  {{ $t(activeFilterMessage) }}
                  <aw-icon icon="chevron-down"/>
                </button>
              </template>
              <template v-slot:dropdown="{ opened, dropdown }">
                <div
                  class="Dropdown absolute flex flex-col gap-y-3 rounded-md shadow-md bg-white w-auto text-sm text-left z-10 px-3 py-1.5"
                  v-show="opened"
                  :ref="dropdown"
                >
                  <button
                    class="text-left"
                    @click.stop.prevent="activeFilter = null"
                  >
                    {{ $t('All') }}
                  </button>
                  <button
                    v-for="filter in usableFilters" :key="filter"
                    class="text-left capitalize"
                    @click.stop.prevent="activeFilter = filter"
                  >
                    {{ $t(filter) }}
                  </button>
                </div>
              </template>
            </aw-dropdown>
          </nav>
        </template>
        <template #right="{ contractRight }">
          <filters-dropdown :enable="contractRight">
            <div 
              class="pill__SearchInput InputGroupFocus w-32 ml-auto"
              ref="searchWrapper"
            >
              <input
                class="form-control"
                type="text"
                :placeholder="$t('Search')"
                v-model="searchQuery"
              />
              <aw-icon 
                v-if="!searchQuery"
                icon="search"
              />
              <button
                v-else
                class="SearchInput__CloseIcon"
                @click.stop.prevent="eraseSearch()"
              >
                <aw-icon 
                  icon="close"
                />
              </button>
            </div>
          </filters-dropdown>
        </template>
      </bc-contractable-menu>
    </template>
    <template v-if="searchedTeams && searchedTeams.length">
      <bc-approval-team-row
        v-for="team in searchedTeams"
        :key="team.id"
        :team="team"
      />
    </template>
    <template v-else-if="searchedApprovers && searchedApprovers.length">
      <bc-approval-approver-row
        v-for="(approver, i) in searchedApprovers"
        :key="i"
        :approver="approver"
      />
    </template>
    <div 
      v-else-if="searchQuery"
      class="BcApprovalsOverview__Empty w-full h-auto flex flex-col justify-center items-center"
    >
      <aw-icon 
        icon="search" 
        class="mx-auto h-12 w-12 text-gray-400"
      />
      <h3 class="mt-2 text-sm font-medium text-gray-400">
        {{ $t('Sorry, we can\'t find') }} 
        <b class="text-bc-blue">
          &bdquo;{{ searchQuery }}&ldquo;
        </b>
      </h3>
    </div>
    <div 
      v-else
      class="BcApprovalsOverview__Empty w-full h-auto flex flex-col justify-center items-center"
    >
      <aw-icon 
        icon="check" 
        class="mx-auto h-12 w-12 text-gray-400"
      />
      <h3 class="mt-2 text-sm font-medium text-gray-400">
        {{ $t('Approvers doesn\'t selected.') }} 
      </h3>
    </div>
  </div>
  <slot name="footer" v-bind="{ callForApprovalMessage, showSendRequestsButton }" />
</template>

<script>
import { search } from 'ss-search'
import { mapStores } from 'pinia'
import useBcDocumentStore from '@/frontapp/store/bcDocument'

export default {
  props: {
    approval: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeFilter: null,
      filters: [
        'approved',
        'disapproved',
        'waiting',
      ],
      searchQuery: null,
    }
  },
  computed: {
    ...mapStores(useBcDocumentStore),
    activeFilterMessage() {
      if (!this.activeFilter) {
        return 'All'
      }

      return this.activeFilter
    },
    showSendRequestsButton() {
      return this.waitingApprovers.length > 0 && this.bcDocumentStore.document.can_edit && !this.bcDocumentStore.document.deleted
    },
    approvers() {
      return this.approval?.approvers ? this.approval.approvers : [] 
    },
    approval_teams() {
      return this.approval?.approval_teams ? this.approval.approval_teams : [] 
    },
    usableFilters() {
      return this.filters.filter((filter) => this[filter + 'Approvers'].length > 0)
    },
    filteredApprovers(){
      const accumulateApprovers = {}
      this.approvers.forEach(approver => {
        if (
          !this.activeFilter ||
          (this.activeFilter === 'waiting' && !approver.approved && !approver.disapproved) ||
          (this.activeFilter === 'approved' && approver.approved) ||
          (this.activeFilter === 'disapproved' && approver.disapproved)
        ) {
          accumulateApprovers[approver.user.id] = approver
        }
      })
      return accumulateApprovers
    },
    filteredApproversBySearch() {
      const searchedApprovers = search(Object.values(this.filteredApprovers), ['user.name', 'user.surname', 'user.email'], this.searchQuery)
      const accumulateApprovers = {}

      searchedApprovers.forEach(approver => {
        accumulateApprovers[approver.user.id] = approver
      }) 

      return accumulateApprovers
    },
    approvedApprovers() {
      return this.approvers.filter(approver => approver.approved)
    },
    disapprovedApprovers() {
      return this.approvers.filter(approver => approver.disapproved)
    },
    waitingApprovers() {
      return this.approvers.filter(approver => !approver.approved && !approver.disapproved)
    },
    callForApprovalMessage() {
      return this.$t('Call for approval') + ' (' + this.waitingApprovers.length + ')'
    },
    searchedApprovers() {
      return Object.values(this.filteredApproversBySearch)
    },
    searchedTeams() {
      const accumulateTeams = []

      if (!this.searchQuery) {
        this.approval_teams.forEach(team => {
          let approvers = this.asignApprovers(team.members, this.filteredApprovers)

          if (!this.activeFilter || (approvers && approvers.length > 0)) {
            accumulateTeams.push({
              ...team,
              approvers,
              open: false
            })
          }
        })

        return accumulateTeams
      }

      this.approval_teams.forEach(team => {
        const teamMatches = search([team], ['name'], this.searchQuery)

        if (teamMatches.length > 0) {
          const approvers = this.asignApprovers(team.members, this.filteredApprovers)

          if (approvers && approvers.length > 0) {
            accumulateTeams.push({
              ...team,
              approvers,
              open: false
            })
          }
        } else {
          const approvers = this.asignApprovers(team.members, this.filteredApproversBySearch)

          if (approvers && approvers.length > 0) {
            accumulateTeams.push({
              ...team,
              approvers,
              open: true
            })
          }
        }
      })
      return accumulateTeams
    },
  },
  methods: {
    eraseSearch() {
      this.searchQuery = ''
    },
    asignApprovers(teamMembers, setOfApprovers) {
      const accumulateApprovers = []

      teamMembers.forEach(id => {
        if (setOfApprovers[id]) {
          accumulateApprovers.push(setOfApprovers[id])
        }
      })

      return accumulateApprovers
    }
  },
}
</script>

<style lang="scss" scoped>
.BcApprovalsOverview__Empty {
  min-height: 20rem;
}
</style>
